import { useState, useMemo, useEffect, useRef } from 'react'
import { Collapse, TableBody, TableHead, useMediaQuery, Checkbox } from '@mui/material'

import { Button } from '../Button'
import { currencyMask } from '@utils/masks/currencyMask'
import { emotionTheme } from '@styles/theme'
import { KeyboardArrowDown } from '@assets/svgs/KeyboardArrowDown'
import { KeyboardArrowUp } from '@assets/svgs/KeyboardArrowUp'
import { Pagination } from './components/Pagination'
import { SortableHeader } from './components/SortableHeader'
import { calculateStickyOffsets } from './helpers/calculateStickyOffsets'
import { extractNumericValue } from './helpers/extractNumericValue'
import { LoadingGrid } from '../LoadingGrid'
import { EmptyIcon } from '@assets/svgs/EmptyIcon'
import { Tooltip } from '../Tooltip'

import type { TableProps, SortDirection } from './types'

import * as S from './styles'

export const Table = ({
  data,
  totalData,
  headerTitle,
  headerPeriod,
  footer,
  fixedRow,
  isLoading,
  onSort,
  selectable,
  onSelectionChange,
  selectionResetKey,
  disabledRows,
  groupedHeaderRow
}: TableProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const [isCloseTable, setIsCloseTable] = useState(true)
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: SortDirection } | null>(null)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const onSelectionChangeRef = useRef(onSelectionChange)

  const columns = data[0] || []

  const columnsWithOffsets = calculateStickyOffsets(columns)

  const handleSort = (columnId: string, direction: SortDirection) => {
    const column = columns.find((col) => col.id === columnId)
    const sortKey = column?.sortKey || columnId

    setSortConfig({ key: sortKey, direction })
    !!onSort &&
      onSort({ sortBy: sortKey, sortType: direction === 'asc' ? 'ASC' : direction === 'desc' ? 'DESC' : null })
  }

  const sortedData = useMemo(() => {
    if (onSort) return data

    if (!sortConfig || sortConfig.direction === 'normal') return data

    return [...data].sort((a, b) => {
      const aValue = a.find((col) => col.id === sortConfig.key)?.value
      const bValue = b.find((col) => col.id === sortConfig.key)?.value

      const aNumeric = extractNumericValue(aValue)
      const bNumeric = extractNumericValue(bValue)

      if (aNumeric !== null && bNumeric !== null) {
        return sortConfig.direction === 'asc' ? aNumeric - bNumeric : bNumeric - aNumeric
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
      }

      const aStr = String(aValue || '')
      const bStr = String(bValue || '')

      return sortConfig.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
    })
  }, [data, sortConfig, onSort])

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange
  }, [onSelectionChange])

  useEffect(() => {
    setSelectedRows(new Set())
    onSelectionChangeRef.current?.([])
  }, [selectionResetKey])

  const disabledSet = new Set(disabledRows ?? [])
  const selectableIndices = sortedData.map((_, i) => i).filter((i) => !disabledSet.has(i))

  const allSelected = selectableIndices.length > 0 && selectableIndices.every((i) => selectedRows.has(i))
  const someSelected = selectableIndices.some((i) => selectedRows.has(i)) && !allSelected

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedRows(new Set())
      onSelectionChange?.([])
    } else {
      setSelectedRows(new Set(selectableIndices))
      onSelectionChange?.(selectableIndices)
    }
  }

  const handleSelectRow = (index: number) => {
    if (disabledSet.has(index)) return
    const newSet = new Set(selectedRows)
    if (newSet.has(index)) {
      newSet.delete(index)
    } else {
      newSet.add(index)
    }
    setSelectedRows(newSet)
    onSelectionChange?.([...newSet])
  }

  return (
    <>
      {!!headerTitle && (
        <S.Header>
          <S.HeaderTitle onClick={() => setIsCloseTable(!isCloseTable)}>
            {headerTitle}
            {isCloseTable ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
          </S.HeaderTitle>

          {!!headerPeriod && <S.HeaderPeriod>{headerPeriod}</S.HeaderPeriod>}
        </S.Header>
      )}

      <S.Container
        style={{
          borderRadius: !!headerTitle ? '0 0 10px 10px' : '10px',
          borderTop: !!headerTitle ? 'none' : `1px solid ${emotionTheme.colors.border.light}`
        }}
      >
        {!!isLoading && !data.length ? (
          <S.GridMessage>
            <LoadingGrid />
          </S.GridMessage>
        ) : !data.length && !isLoading ? (
          <S.GridMessage>
            <EmptyIcon />
            <S.EmptyMessage>Nenhum dado encontrado!</S.EmptyMessage>
          </S.GridMessage>
        ) : (
          <>
            <Collapse in={isCloseTable} unmountOnExit>
              <S.TableWrapper>
                {!!isLoading && !!data.length && (
                  <S.LoaderBox>
                    <LoadingGrid />
                  </S.LoaderBox>
                )}
                <S.TableContent>
                  <S.Table style={{ tableLayout: 'auto', minWidth: '100%' }}>
                    <TableHead>
                      {!!groupedHeaderRow && (
                        <S.TableRow>
                          {selectable && (
                            <S.HeadCell
                              key="checkbox-header-group"
                              columnQuantity={columns.length + 1}
                              align="center"
                              minWidth="3rem"
                              maxWidth="3rem"
                              sticky={false}
                              style={{
                                background: !!headerTitle
                                  ? emotionTheme.colors.gray[100]
                                  : emotionTheme.colors.blue.head
                              }}
                            />
                          )}
                          {groupedHeaderRow.map((group, index) => (
                            <S.HeadCell
                              key={index}
                              colSpan={group.colSpan}
                              columnQuantity={columns.length}
                              align="center"
                              style={{
                                background: group.background
                                  ? group.background
                                  : !!headerTitle
                                    ? emotionTheme.colors.gray[100]
                                    : emotionTheme.colors.blue.head,
                                ...(group.divider && {
                                  borderBottom: `1px solid ${emotionTheme.colors.border.light}`
                                })
                              }}
                            >
                              {group.label}
                            </S.HeadCell>
                          ))}
                        </S.TableRow>
                      )}
                      <S.TableRow>
                        {selectable && (
                          <S.HeadCell
                            key="checkbox-header"
                            columnQuantity={columns.length + 1}
                            align="center"
                            minWidth="3rem"
                            maxWidth="3rem"
                            sticky={false}
                            style={{
                              background: !!headerTitle
                                ? emotionTheme.colors.gray[100]
                                : emotionTheme.colors.blue.head
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={allSelected}
                              indeterminate={someSelected}
                              onChange={handleSelectAll}
                            />
                          </S.HeadCell>
                        )}
                        {columnsWithOffsets.map((column) => {
                          const headerAlignment = column.headerAlign || column.align
                          return (
                            <S.HeadCell
                              key={column.id}
                              columnQuantity={columns.length}
                              align={headerAlignment}
                              minWidth={column.minWidth || '10rem'}
                              maxWidth={column.maxWidth || '14.5rem'}
                              sticky={!!column.stickyPosition}
                              stickyPosition={column.stickyPosition}
                              stickyOffset={column.stickyOffset}
                              style={{
                                background: column.columnColor
                                  ? column.columnColor
                                  : !!headerTitle
                                    ? emotionTheme.colors.gray[100]
                                    : emotionTheme.colors.blue.head
                              }}
                            >
                              <SortableHeader
                                sortable={column.sortable}
                                align={headerAlignment}
                                onSort={(direction) => handleSort(column.id, direction)}
                                isActive={sortConfig?.key === (column.sortKey || column.id)}
                                currentSortDirection={
                                  sortConfig?.key === (column.sortKey || column.id) ? sortConfig.direction : 'normal'
                                }
                              >
                                {column.columnName}
                              </SortableHeader>
                            </S.HeadCell>
                          )
                        })}
                      </S.TableRow>
                    </TableHead>

                    <TableBody>
                      {sortedData.map((row, rowIndex) => {
                        const rowWithOffsets = calculateStickyOffsets(row)

                        return (
                          <S.TableRow key={rowIndex}>
                            {selectable && (
                              <S.TableCell
                                key={`${rowIndex}-checkbox`}
                                columnQuantity={columns.length + 1}
                                align="center"
                                minWidth="3rem"
                                maxWidth="3rem"
                                sticky={false}
                              >
                                <Checkbox
                                  size="small"
                                  checked={selectedRows.has(rowIndex)}
                                  disabled={disabledSet.has(rowIndex)}
                                  onChange={() => handleSelectRow(rowIndex)}
                                />
                              </S.TableCell>
                            )}
                            {rowWithOffsets.map((col) => {
                              const rawValue = col.value
                              const numericValue = Number(rawValue)
                              const isNegative = !isNaN(numericValue) && numericValue < 0
                              const withoutTooltip = col.withoutTooltip
                              const bodyAlignment = col.bodyAlign || col.align

                              return (
                                <S.TableCell
                                  key={`${rowIndex}-${col.id}`}
                                  columnQuantity={columns.length}
                                  align={bodyAlignment}
                                  isNegative={isNegative}
                                  minWidth={col.minWidth || '10rem'}
                                  maxWidth={col.maxWidth || '14.5rem'}
                                  sticky={!!col.stickyPosition}
                                  stickyPosition={col.stickyPosition}
                                  stickyOffset={col.stickyOffset}
                                  columnColor={col.bodyColumnColor || col.columnColor}
                                >
                                  <Tooltip
                                    title={
                                      !withoutTooltip
                                        ? col.currency
                                          ? currencyMask(Number(rawValue).toFixed(2))
                                          : rawValue
                                        : ''
                                    }
                                    placement="top"
                                    delay={1000}
                                  >
                                    <S.TableCellValue>
                                      {col.currency ? currencyMask(Number(rawValue).toFixed(2)) : rawValue}
                                    </S.TableCellValue>
                                  </Tooltip>
                                </S.TableCell>
                              )
                            })}
                          </S.TableRow>
                        )
                      })}
                    </TableBody>

                    {!!totalData && (
                      <TableBody>
                        {totalData.map((row, rowIndex) => {
                          const rowWithOffsets = calculateStickyOffsets(row)
                          // Rótulo com colSpan (ex.: "Total Registros") absorve a coluna do checkbox,
                          // ficando alinhado à esquerda da tabela em vez de após o placeholder.
                          const labelAbsorbsCheckbox = !!selectable && !!rowWithOffsets[0]?.colSpan

                          return (
                            <S.TableRow key={rowIndex}>
                              {selectable && !labelAbsorbsCheckbox && (
                                <S.TableCell
                                  key={`${rowIndex}-total-checkbox-placeholder`}
                                  columnQuantity={columns.length + 1}
                                  align="center"
                                  minWidth="3rem"
                                  maxWidth="3rem"
                                  sticky={false}
                                />
                              )}
                              {rowWithOffsets.map((col, colIndex) => {
                                const rawValue = col.value
                                const numericValue = Number(rawValue)
                                const isNegative = !isNaN(numericValue) && numericValue < 0
                                const withoutTooltip = col.withoutTooltip
                                const bodyAlignment = col.bodyAlign || col.align
                                const colSpan =
                                  labelAbsorbsCheckbox && colIndex === 0 ? (col.colSpan ?? 0) + 1 : col.colSpan

                                return (
                                  <S.TableCell
                                    key={`${rowIndex}-${col.id}`}
                                    columnQuantity={columns.length}
                                    align={bodyAlignment}
                                    isNegative={isNegative}
                                    minWidth={col.minWidth || '10rem'}
                                    maxWidth={col.maxWidth || '14.5rem'}
                                    sticky={!!col.stickyPosition}
                                    stickyPosition={col.stickyPosition}
                                    stickyOffset={col.stickyOffset}
                                    columnColor={col.bodyColumnColor || col.columnColor}
                                    fontWeight={col.fontWeight}
                                    colSpan={colSpan}
                                  >
                                    <Tooltip
                                      title={
                                        !withoutTooltip
                                          ? col.currency
                                            ? currencyMask(Number(rawValue).toFixed(2))
                                            : rawValue
                                          : ''
                                      }
                                      placement="top"
                                      delay={1000}
                                    >
                                      <S.TableCellValue>
                                        {col.currency ? currencyMask(Number(rawValue).toFixed(2)) : rawValue}
                                      </S.TableCellValue>
                                    </Tooltip>
                                  </S.TableCell>
                                )
                              })}
                            </S.TableRow>
                          )
                        })}
                      </TableBody>
                    )}
                  </S.Table>
                </S.TableContent>
              </S.TableWrapper>
            </Collapse>

            {!!fixedRow && <S.AditionalRow>{fixedRow.element}</S.AditionalRow>}
          </>
        )}

        {(!!data.length || !isLoading) &&
          (!!footer?.actionButton?.labelButton ||
            !!footer?.secondActionButton?.labelButton ||
            (!!footer?.pagination?.totalItems && footer?.pagination?.totalItems > 5)) && (
            <>
              <S.Footer>
                <S.FooterActions>
                  {footer.actionButton && (
                    <Tooltip
                      width={!isMobile ? 'fit-content' : '100%'}
                      title={footer.actionButton.tooltip}
                      placement="top-start"
                      style={{ width: isMobile ? '100%' : 'fit-content' }}
                    >
                      <Button
                        width={isMobile ? '100%' : 'fit-content'}
                        colorType="outlineBlue"
                        onClick={footer.actionButton.onClick}
                        disabled={isLoading || footer.actionButton.disabled}
                        rightIcon={footer.actionButton.rightIcon}
                      >
                        {footer.actionButton.labelButton}
                      </Button>
                    </Tooltip>
                  )}
                  {footer.secondActionButton && (
                    <Tooltip
                      width={!isMobile ? 'fit-content' : '100%'}
                      title={footer.secondActionButton.tooltip}
                      placement="top-start"
                      style={{ width: isMobile ? '100%' : 'fit-content' }}
                    >
                      <Button
                        width={isMobile ? '100%' : 'fit-content'}
                        colorType="outlineBlue"
                        onClick={footer.secondActionButton.onClick}
                        disabled={isLoading || footer.secondActionButton.disabled}
                      >
                        {footer.secondActionButton.labelButton}
                      </Button>
                    </Tooltip>
                  )}
                </S.FooterActions>

                {!!footer.pagination?.totalItems && footer.pagination?.totalItems > 5 && (
                  <Pagination
                    currentPage={footer.pagination?.currentPage}
                    itemsPerPage={footer.pagination?.itemsPerPage}
                    totalItems={footer.pagination?.totalItems}
                    totalPages={footer.pagination?.totalPages}
                    onPageChange={footer.pagination?.onPageChange}
                    onItemsPerPageChange={footer.pagination?.onItemsPerPageChange}
                  />
                )}
              </S.Footer>
            </>
          )}
      </S.Container>
    </>
  )
}
